import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'

export interface CigarBox {
  id: string
  cigarLineId: string
  humidorId: string
  boxSize: number
  cigarsPerBox: number
  purchasePrice?: number | string
  purchaseDate?: string
  purchaseLocation?: string
  notes?: string
  status: 'sealed' | 'opened' | 'finished'
  imageUrls: string[]
  createdAt: string
  updatedAt: string
  cigarLine: {
    id: string
    name: string
    brand: {
      id: string
      name: string
      country?: string
    }
  }
  humidor: {
    id: string
    name: string
  }
}

export interface CreateBoxRequest {
  cigarLineId: string
  humidorId: string
  boxSize: number
  cigarsPerBox: number
  purchasePrice?: number
  purchaseDate?: string
  purchaseLocation?: string
  notes?: string
}

export interface UpdateBoxStatusRequest {
  status: 'sealed' | 'opened' | 'finished'
}

export interface BoxSearchParams {
  status?: string
  humidorId?: string
  page?: number
  limit?: number
}

export interface BoxSearchResponse {
  success: boolean
  data: CigarBox[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

export const boxApi = createApi({
  reducerPath: 'boxApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/boxes',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['Box'],
  endpoints: (builder) => ({
    getBoxes: builder.query<BoxSearchResponse, BoxSearchParams>({
      query: (params) => ({
        url: '',
        params,
      }),
      providesTags: ['Box'],
    }),
    getBox: builder.query<CigarBox, string>({
      query: (id) => `/${id}`,
      transformResponse: (response: { success: boolean; data: CigarBox }) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'Box', id }],
    }),
    createBox: builder.mutation<CigarBox, CreateBoxRequest>({
      query: (box) => ({
        url: '',
        method: 'POST',
        body: box,
      }),
      transformResponse: (response: { success: boolean; data: CigarBox }) => response.data,
      invalidatesTags: ['Box'],
    }),
    updateBoxStatus: builder.mutation<CigarBox, { id: string; status: UpdateBoxStatusRequest['status'] }>({
      query: ({ id, status }) => ({
        url: `/${id}/status`,
        method: 'PATCH',
        body: { status },
      }),
      transformResponse: (response: { success: boolean; data: CigarBox }) => response.data,
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Box', id }],
    }),
  }),
})

export const {
  useGetBoxesQuery,
  useGetBoxQuery,
  useCreateBoxMutation,
  useUpdateBoxStatusMutation,
} = boxApi
